import {
  Button,
  Card,
  Flex,
  RadioGroup,
  RadioTile,
} from '@utilitywarehouse/hearth-react';
import SpotBookAppointment from '@utilitywarehouse/hearth-svg-assets/lib/spot-book-appointment-light.svg';
import SpotHelp from '@utilitywarehouse/hearth-svg-assets/lib/spot-help-light.svg';
import SpotNeighbours from '@utilitywarehouse/hearth-svg-assets/lib/spot-neighbours-light.svg';
import SpotOnlineAccount from '@utilitywarehouse/hearth-svg-assets/lib/spot-online-account-light.svg';
import { FormikProps, useFormikContext } from 'formik';

import { ReferralField } from '@components/ReferralField';
import { AppointmentType } from '@generated/gql/graphql';
import { useFieldProps } from '@hooks/useFieldProps';
import { BooleanSelection, SelectOptions } from '@src/types/index';

import { RestoreQuotes } from './RestoreQuotes';
import { SupportLoginModal } from './SupportLoginModal';
import './styles.scss';
import { GETTING_QUOTE, PartnerFormValues, SUPPORT_REMOTE } from './types';

type PartnerFormProps = {
  supportingLoginError: string;
  connectorsOptions: Nullable<SelectOptions>;
};

export const PartnerForm = ({
  supportingLoginError,
  connectorsOptions,
}: PartnerFormProps) => {
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
    submitForm,
    isValid,
    setFieldValue,
    handleSubmit,
  }: FormikProps<PartnerFormValues> = useFormikContext();

  const getFieldProps = useFieldProps<PartnerFormValues>({
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  });

  const continueWithoutSupport = () => {
    setFieldValue('hasSupportingPartner', BooleanSelection.No);

    submitForm();
  };

  const isSupportingAnotherApptSelected =
    values.appointmentType === SUPPORT_REMOTE;
  const isGettingQuoteSelected = values.appointmentType === GETTING_QUOTE;
  const hasSupportingPartnerSelected =
    values.hasSupportingPartner === BooleanSelection.Yes;
  const displaySupportJoinField =
    !isSupportingAnotherApptSelected && !isGettingQuoteSelected;

  const getButtonText = () => {
    if (isGettingQuoteSelected) {
      return 'Get quote';
    }
    if (isSupportingAnotherApptSelected) {
      return 'Join appointment';
    }
    return 'Start presentation';
  };

  const captureConnectorReferral = !!(
    connectorsOptions && connectorsOptions.length > 0
  );

  return (
    <form onSubmit={handleSubmit}>
      <Flex className="partnerAppointment__form" spacing="xl">
        <RadioGroup
          {...getFieldProps('appointmentType')}
          onValueChange={(val: string) => setFieldValue('appointmentType', val)}
          label="What would you like to do?"
          className="partnerAppointment__radioField partnerAppointment__typeField"
        >
          <RadioTile
            label="Start a face-to-face appointment"
            value={AppointmentType.FaceToFace}
            image={<img src={SpotNeighbours} alt="" />}
            data-testid="partner-setup-face-to-face"
          />
          <RadioTile
            label="Start a remote appointment"
            value={AppointmentType.PartnerRemote}
            image={<img src={SpotOnlineAccount} alt="" />}
            data-testid="partner-setup-remote"
          />
          <RadioTile
            label="Support another Partner remotely"
            value={SUPPORT_REMOTE}
            image={<img src={SpotHelp} alt="" />}
            data-testid="support-remote"
          />
          <RadioTile
            label="I’m just getting a quote"
            value={GETTING_QUOTE}
            image={<img src={SpotBookAppointment} alt="" />}
            data-testid="getting-quote"
          />
        </RadioGroup>
        {(!isSupportingAnotherApptSelected ||
          hasSupportingPartnerSelected ||
          displaySupportJoinField) && (
          <Card
            variant="subtle"
            colorScheme="neutralStrong"
            direction="column"
            spacing="xl"
          >
            {displaySupportJoinField && (
              <RadioGroup
                className="partnerAppointment__radioField"
                {...getFieldProps('hasSupportingPartner')}
                label="Do you have a supporting Partner joining this appointment?"
                direction="row"
                onValueChange={(val: string) =>
                  setFieldValue('hasSupportingPartner', val)
                }
              >
                <RadioTile
                  label="Yes"
                  value={BooleanSelection.Yes}
                  data-testid="supporting-partner-yes"
                />

                <RadioTile
                  label="No"
                  value={BooleanSelection.No}
                  data-testid="supporting-partner-no"
                />
              </RadioGroup>
            )}
            {displaySupportJoinField && hasSupportingPartnerSelected && (
              <RadioGroup
                className="partnerAppointment__radioField"
                {...getFieldProps('supportingPartnerType')}
                label="Is the supporting Partner joining face to face or remotely?"
                direction="row"
                onValueChange={(val: string) =>
                  setFieldValue('supportingPartnerType', val)
                }
              >
                <RadioTile
                  label="Face to face"
                  value={AppointmentType.FaceToFace}
                  data-testid="supporting-partner-face-to-face"
                />
                <RadioTile
                  label="Remotely"
                  value={AppointmentType.PartnerRemote}
                  data-testid="supporting-partner-remote"
                />
              </RadioGroup>
            )}
            {!isSupportingAnotherApptSelected &&
              !values.restoredQuoteAlreadyAttributed && (
                <ReferralField
                  captureConnectorReferral={captureConnectorReferral}
                  label={`Were you introduced to this potential customer by an existing UW customer${
                    captureConnectorReferral ? ' or UW connector' : ''
                  }?`}
                  connectorsOptions={connectorsOptions}
                />
              )}

            {!isSupportingAnotherApptSelected && <RestoreQuotes />}
          </Card>
        )}
        <SupportLoginModal
          supportingLoginError={supportingLoginError}
          continueWithoutSupport={continueWithoutSupport}
        />
        <Flex
          direction={{
            desktop: 'row-reverse',
            tablet: 'row-reverse',
            mobile: 'column',
          }}
        >
          <Button
            type="submit"
            disabled={!isValid}
            data-testid="partner-setup-submit"
          >
            {getButtonText()}
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};
